import React from "react";
import PropTypes from "prop-types";
import cname from "./style.module.scss";
import { RenderContents } from "./RenderContents";

/**
 *  ProgressStepperWidget
 *  - Tabbed widget that displays contents of each tab at a time
 *  - Tabs could contain components typically displayed inside a Notebook
 *  - Has a callback to when the last tab is reached
 */
export function ProgressStepperWidget(props) {
  const onTabChange = (tabId) => () => props.onTabChange(tabId);

  const setClassName = (tab) => {
    return [
      cname.tabItem,
      tab.visible ? cname.visible : tab.visited ? cname.visited : "",
    ].join(" ");
  };

  const renderTab = (tab) => (
    <li
      role="option"
      aria-selected={tab.label}
      tabIndex={0}
      key={tab.id}
      className={setClassName(tab)}
      onClick={() => onTabChange(tab.id)}
      onKeyPress={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          onTabChange(tab.id);
        }
      }}
    >
      <p>{tab.label}</p>
      <span />
    </li>
  );

  const isCurrentTab = (tab) => tab.visible;

  const contentProps = () => {
    const { id: tabId, contents } = props.tabs.find(isCurrentTab);
    return {
      contents,
      tabId,
    };
  };

  return (
    <div className={cname.widget}>
      <ul className={cname.tabList}> {props.tabs.map(renderTab)}</ul>
      <div className={cname.content}>
        <RenderContents {...contentProps()} components={props.components} />
      </div>
    </div>
  );
}

ProgressStepperWidget.propTypes = {
  /**
   * List of tabs and components to be renderd on them
   */
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * Unique for the tab
       */
      id: PropTypes.number,
      /**
       * Label for the tab
       */
      label: PropTypes.string,
      /**
       * Boolean to set if tab is visible
       */
      visible: PropTypes.bool,
      /**
       * Boolean to set if tab is visited
       */
      visited: PropTypes.bool,
      /**
       * List of components to be rendered in the tab
       */
      contents: PropTypes.arrayOf(PropTypes.object),
    })
  ),
  /**
   * Callback to change visible tab
   */
  onTabChange: PropTypes.func,
  /**
   * All React Components needed to render Content Block
   */
  components: PropTypes.object,
};

ProgressStepperWidget.defaultProps = {
  tabs: [],
};
